import React, { useState, useRef } from 'react';
import {
  ChevronDown, ChevronUp, User, Code, BookOpen, Handshake,
  Pencil, GripVertical, Plus, X, Trash2,
} from 'lucide-react';
import { Tribe, Person, Process } from '../data';
import { UpdatePerson, AddProcess, UpdateProcess, DeleteProcess, UpdateKR } from '../hooks/useOrgState';
import InlineEdit from './InlineEdit';
import OKRSection from './OKRSection';
import { useAuth } from '../context/AuthContext';
import PermissionGate from './auth/PermissionGate';

interface Props {
  tribe: Tribe;
  coordinator: Person;
  cycle: string;
  updatePerson: UpdatePerson;
  addProcess: AddProcess;
  updateProcess: UpdateProcess;
  deleteProcess: DeleteProcess;
  updateKR: UpdateKR;
}

// ── ProcessCard ────────────────────────────────────────────

interface ProcessCardProps {
  process: Process;
  tribeId: string;
  isNew: boolean;
  updateProcess: UpdateProcess;
  deleteProcess: DeleteProcess;
  onNewCancel: () => void;
  onNewSave: () => void;
}

function ProcessCard({
  process, tribeId, isNew,
  updateProcess, deleteProcess,
  onNewCancel, onNewSave,
}: ProcessCardProps) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('editor');

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(isNew && canEdit);
  const [draftTitle, setDraftTitle] = useState(process.title);
  const [draftDesc, setDraftDesc] = useState(process.description);
  const [draftSteps, setDraftSteps] = useState<string[]>(
    process.steps.length ? [...process.steps] : ['']
  );
  const [dragIdx, setDragIdx] = useState<number | null>(null);
  const [dropIdx, setDropIdx] = useState<number | null>(null);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const dragFrom = useRef<number | null>(null);

  function startEdit() {
    setDraftTitle(process.title);
    setDraftDesc(process.description);
    setDraftSteps(process.steps.length ? [...process.steps] : ['']);
    setConfirmDelete(false);
    setEditing(true);
  }

  function handleSave() {
    const cleanSteps = draftSteps.map(s => s.trim()).filter(Boolean);
    updateProcess(tribeId, process.id, {
      title: draftTitle.trim() || 'Novo processo',
      description: draftDesc.trim(),
      steps: cleanSteps,
    });
    if (isNew) onNewSave();
    setEditing(false);
  }

  function handleCancel() {
    if (isNew) {
      deleteProcess(tribeId, process.id);
      onNewCancel();
    } else {
      setEditing(false);
    }
  }

  function handleDelete() {
    deleteProcess(tribeId, process.id);
    if (isNew) onNewCancel();
  }

  function handleDrop(toIdx: number) {
    const from = dragFrom.current;
    if (from === null || from === toIdx) return;
    setDraftSteps(prev => {
      const s = [...prev];
      const [moved] = s.splice(from, 1);
      s.splice(toIdx, 0, moved);
      return s;
    });
  }

  function updateStep(i: number, val: string) {
    setDraftSteps(prev => { const s = [...prev]; s[i] = val; return s; });
  }

  function removeStep(i: number) {
    setDraftSteps(prev => prev.filter((_, idx) => idx !== i));
  }

  function insertStepAfter(i: number) {
    setDraftSteps(prev => {
      const s = [...prev];
      s.splice(i + 1, 0, '');
      return s;
    });
  }

  // ── Edit mode ──────────────────────────────────────────
  if (editing) {
    return (
      <div className="process-card process-card-editing">
        <div className="process-edit-form">
          <input
            className="process-edit-field process-edit-title-input"
            value={draftTitle}
            onChange={e => setDraftTitle(e.target.value)}
            placeholder="Título do processo"
            autoFocus
          />
          <textarea
            className="process-edit-field process-edit-desc-input"
            value={draftDesc}
            onChange={e => setDraftDesc(e.target.value)}
            placeholder="Descrição (opcional)"
            rows={2}
          />

          <div className="process-edit-steps-label">Etapas</div>
          <div className="process-edit-steps">
            {draftSteps.map((step, i) => (
              <div
                key={i}
                className={`edit-step${dragIdx === i ? ' dragging' : ''}${dropIdx === i && dragIdx !== i ? ' drop-target' : ''}`}
                draggable
                onDragStart={() => { dragFrom.current = i; setDragIdx(i); }}
                onDragOver={e => { e.preventDefault(); if (dragFrom.current !== i) setDropIdx(i); }}
                onDrop={() => { handleDrop(i); setDragIdx(null); setDropIdx(null); dragFrom.current = null; }}
                onDragEnd={() => { setDragIdx(null); setDropIdx(null); dragFrom.current = null; }}
              >
                <GripVertical size={14} className="step-drag-handle" />
                <input
                  className="edit-step-input"
                  value={step}
                  onChange={e => updateStep(i, e.target.value)}
                  placeholder={`Etapa ${i + 1}`}
                  onKeyDown={e => {
                    if (e.key === 'Enter') { e.preventDefault(); insertStepAfter(i); }
                    if (e.key === 'Backspace' && step === '' && draftSteps.length > 1) {
                      e.preventDefault();
                      removeStep(i);
                    }
                  }}
                />
                {draftSteps.length > 1 && (
                  <button className="step-remove-btn" onClick={() => removeStep(i)} tabIndex={-1}>
                    <X size={13} />
                  </button>
                )}
              </div>
            ))}
          </div>

          <button className="add-step-btn" onClick={() => setDraftSteps(s => [...s, ''])}>
            <Plus size={13} />
            Adicionar etapa
          </button>

          <div className="process-edit-actions">
            {!isNew && (
              confirmDelete ? (
                <>
                  <span className="delete-confirm-label">Excluir processo?</span>
                  <button className="btn-cancel-delete" onClick={() => setConfirmDelete(false)}>Não</button>
                  <button className="btn-confirm-delete" onClick={handleDelete}>Confirmar</button>
                </>
              ) : (
                <button className="btn-delete-process" onClick={() => setConfirmDelete(true)}>
                  <Trash2 size={13} />
                  Excluir
                </button>
              )
            )}
            <div className="edit-actions-spacer" />
            <button className="btn-cancel-process" onClick={handleCancel}>Cancelar</button>
            <button className="btn-save-process" onClick={handleSave}>Salvar</button>
          </div>
        </div>
      </div>
    );
  }

  // ── View mode ──────────────────────────────────────────
  return (
    <div className={`process-card${open ? ' open' : ''}`}>
      <div className="process-header-row">
        <button className="process-header-content" onClick={() => setOpen(o => !o)}>
          <div className="process-title">{process.title || 'Sem título'}</div>
          <div className="process-meta">{process.description}</div>
        </button>
        <div className="process-header-end">
          {canEdit && (
            <button className="process-edit-btn" onClick={startEdit} title="Editar processo">
              <Pencil size={14} />
            </button>
          )}
          <button className="process-chevron-btn" onClick={() => setOpen(o => !o)}>
            {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </button>
        </div>
      </div>
      {open && process.steps.length > 0 && (
        <div className="process-steps">
          {process.steps.map((step, i) => (
            <div key={i} className="process-step">
              <div className="step-num">{i + 1}</div>
              <div className="step-text">{step}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── MemberCard ─────────────────────────────────────────────

function MemberCard({
  person,
  status,
  updatePerson,
  disabled,
}: {
  person: Person;
  status: 'active' | 'pending';
  updatePerson: UpdatePerson;
  disabled?: boolean;
}) {
  return (
    <div className={`member-card ${status}`}>
      <div className="member-avatar">{person.name[0]}</div>
      <div className="member-info">
        <div className="member-name">
          <InlineEdit disabled={disabled} value={person.name} onChange={v => updatePerson(person.id, 'name', v)} />
        </div>
        <div className="member-level">
          <InlineEdit disabled={disabled} value={person.level ?? ''} onChange={v => updatePerson(person.id, 'level', v)} />
        </div>
      </div>
      {status === 'pending' && <span className="member-badge">reorganização</span>}
      {status === 'active' && <span className="member-badge active">no setor</span>}
    </div>
  );
}

// ── TribeView ──────────────────────────────────────────────

export default function TribeView({
  tribe, coordinator, cycle, updatePerson,
  addProcess, updateProcess, deleteProcess, updateKR,
}: Props) {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('editor');
  const [newProcessId, setNewProcessId] = useState<string | null>(null);

  function handleAddProcess() {
    const id = `proc-${Date.now()}`;
    const newProc: Process = {
      id,
      title: '',
      description: '',
      steps: [''],
      owner: 'analista',
    };
    addProcess(tribe.id, newProc);
    setNewProcessId(id);
  }

  return (
    <div className="tribe-view">
      <div className={`tribe-hero tribe-hero-${tribe.color}`}>
        <div className="hero-label">Tribo</div>
        <h1>{tribe.name}</h1>
        <p>{tribe.tagline}</p>
      </div>

      <div className="tribe-content">
        {/* OKRs */}
        <OKRSection tribe={tribe} cycle={cycle} updateKR={updateKR} />

        {/* Time */}
        <section className="tribe-section">
          <div className="section-header">
            <User size={16} />
            <h2>Time</h2>
          </div>
          <div className="members-grid">
            <div className="member-group">
              <div className="member-group-label"><Code size={14} /> Desenvolvimento</div>
              {tribe.members.devs.map(p => (
                <MemberCard key={p.id} person={p} status="pending" updatePerson={updatePerson} disabled={!canEdit} />
              ))}
            </div>
            <div className="member-group">
              <div className="member-group-label"><BookOpen size={14} /> Produto (PO)</div>
              {tribe.members.po.map(p => (
                <MemberCard key={p.id} person={p} status="pending" updatePerson={updatePerson} disabled={!canEdit} />
              ))}
            </div>
            <div className="member-group">
              <div className="member-group-label"><User size={14} /> Analistas</div>
              {tribe.members.analistas.map(p => (
                <MemberCard
                  key={p.id} person={p}
                  status={tribe.id === 'cloud' ? 'active' : 'pending'}
                  updatePerson={updatePerson}
                  disabled={!canEdit}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Parceiros */}
        <section className="tribe-section">
          <div className="section-header">
            <Handshake size={16} />
            <h2>Parceiros</h2>
          </div>
          <div className="partners-grid">
            {tribe.partners.map(p => (
              <div key={p.name} className={`partner-card partner-card-${p.type}`}>
                <div className="partner-name">{p.name}</div>
                <div className="partner-type">{p.type}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Processos */}
        <section className="tribe-section">
          <div className="section-header">
            <BookOpen size={16} />
            <h2>Processos</h2>
            <span className="section-badge">clique para expandir · lápis para editar</span>
          </div>
          <div className="processes-list">
            {tribe.processes.map(p => (
              <ProcessCard
                key={p.id}
                process={p}
                tribeId={tribe.id}
                isNew={p.id === newProcessId}
                updateProcess={updateProcess}
                deleteProcess={deleteProcess}
                onNewCancel={() => setNewProcessId(null)}
                onNewSave={() => setNewProcessId(null)}
              />
            ))}
            <PermissionGate required="editor">
              <button className="add-process-btn" onClick={handleAddProcess}>
                <Plus size={14} />
                Novo processo
              </button>
            </PermissionGate>
          </div>
        </section>
      </div>
    </div>
  );
}
